import { Button, ButtonStyle } from '@atlas/components';

import { realityCheckPingRequest } from 'api/responsibleGaming';
import classNames from 'classnames';
import type { RealityCheckPopupDataType } from 'config/types';
import { type FC, useCallback } from 'react';
import { useClientProfile } from 'store';
import { AUTH_PAGES, useAuthRedirects } from 'utils/authRedirects';
import useLayout, { LayoutType } from 'utils/layout';
import { Translation, useTranslation } from 'utils/translations';
import useLazyRequest from 'utils/useLazyRequest';
import { useLocation } from 'wouter';
import type { PopupProps } from '../types';
import { getLimitFromMilliseconds } from './getLimitFromMilliseconds';

import styles from './RealityCheckPopup.module.scss';

const RealityCheckPopup: FC<PopupProps<RealityCheckPopupDataType>> = ({
  closePopup,
  data,
}) => {
  const [{ sessionToken }, { logout }] = useClientProfile();
  const { redirectToAuthPage } = useAuthRedirects();
  const [, navigate] = useLocation();
  const layout = useLayout();
  const { execute } = useLazyRequest(realityCheckPingRequest);
  const translatedText = useTranslation('reality_check_modal_change');
  const parts = translatedText.split(/ (?=")/);

  const handleClickStayIn = useCallback(() => {
    execute({
      headers: {
        authorization: `Bearer ${sessionToken}`,
      },
    });
    closePopup();
  }, [execute, sessionToken, closePopup]);

  const selectedLimit = getLimitFromMilliseconds(data?.period);
  const goToRealityCheckSettings = useCallback(() => {
    closePopup();
    navigate('/account/responsible-gaming/reality-check');
  }, [closePopup, navigate]);

  return (
    <div
      className={classNames(styles.wrapper, {
        mobile: layout === LayoutType.mobile,
      })}
    >
      <h3 className={styles.title}>
        <Translation message="title_reality_check" />
      </h3>
      <p className={styles.text}>
        <Translation
          message="reality_check_modal_message"
          params={{
            time: selectedLimit.text,
          }}
        />
      </p>
      <p className={styles.text}>
        {parts[0]}{' '}
        <span
          onKeyDown={goToRealityCheckSettings}
          onClick={goToRealityCheckSettings}
          className={styles['reality-check-link']}
        >
          {parts[1]}
        </span>
      </p>
      <div
        className={classNames(styles.buttons, {
          [styles['buttons-mobile']]: layout === LayoutType.mobile,
        })}
      >
        <Button
          onClick={() => {
            logout();
            closePopup();
            redirectToAuthPage(AUTH_PAGES.signIn);
          }}
          // className={styles.logout}
          style={ButtonStyle.outline}
          data-testid="logout"
        >
          <Translation message="modal_button_logout" />
        </Button>
        <Button onClick={handleClickStayIn} data-testid="stay-in">
          <Translation message="modal_button_close" />
        </Button>
      </div>
    </div>
  );
};

export default RealityCheckPopup;
